"use client"

import { useEffect, useState, useRef } from "react"

interface DroppodPortraitSectionProps {
  scale: number
  hasClone: boolean
  showCloneParticles?: boolean
}

interface Particle {
  id: number
  x: number
  y: number
  vx: number
  vy: number
  size: number
  opacity: number
  life: number
}

export const DroppodPortraitSection = ({
  scale,
  hasClone,
  showCloneParticles = false,
}: DroppodPortraitSectionProps) => {
  const effectiveScale = scale > 0 ? scale : 1
  const width = 84 * effectiveScale
  const height = 84 * effectiveScale
  const left = 19 * effectiveScale
  const top = 26 * effectiveScale

  const [particles, setParticles] = useState<Particle[]>([])
  const particleIdRef = useRef(0)

  // Spawn particles when clone is purchased - surrounding burst effect
  useEffect(() => {
    if (!showCloneParticles) return

    // Create burst of particles from edges surrounding the image
    const newParticles: Particle[] = []
    const particleCount = 40 // More particles for bigger effect

    for (let i = 0; i < particleCount; i++) {
      // Spawn particles from the perimeter of the portrait
      const angle = (i / particleCount) * Math.PI * 2
      const edgeRadius = Math.max(width, height) / 2 + 5 // Start from edge
      const startX = width / 2 + Math.cos(angle) * edgeRadius * 0.8
      const startY = height / 2 + Math.sin(angle) * edgeRadius * 0.8

      // Move inward initially, then outward
      const outwardSpeed = 1.5 + Math.random() * 2

      newParticles.push({
        id: particleIdRef.current++,
        x: startX,
        y: startY,
        vx: Math.cos(angle) * outwardSpeed,
        vy: Math.sin(angle) * outwardSpeed,
        size: 4 + Math.random() * 6, // Bigger particles
        opacity: 1,
        life: 1.2, // Longer life
      })

      // Add some particles from center for extra effect
      if (i % 3 === 0) {
        const centerAngle = Math.random() * Math.PI * 2
        const centerSpeed = 2 + Math.random() * 3
        newParticles.push({
          id: particleIdRef.current++,
          x: width / 2,
          y: height / 2,
          vx: Math.cos(centerAngle) * centerSpeed,
          vy: Math.sin(centerAngle) * centerSpeed,
          size: 5 + Math.random() * 5,
          opacity: 1,
          life: 1,
        })
      }
    }
    setParticles(prev => [...prev, ...newParticles])
  }, [showCloneParticles, width, height])

  // Animate particles
  useEffect(() => {
    if (particles.length === 0) return

    const interval = setInterval(() => {
      setParticles(prev =>
        prev
          .map(p => ({
            ...p,
            x: p.x + p.vx,
            y: p.y + p.vy,
            vy: p.vy + 0.05, // gravity
            life: p.life - 0.02,
            opacity: p.life,
          }))
          .filter(p => p.life > 0)
      )
    }, 16)

    return () => clearInterval(interval)
  }, [particles.length])

  // Continuous ambient particles when clone is present
  const [ambientParticles, setAmbientParticles] = useState<Particle[]>([])
  const ambientIdRef = useRef(0)

  // Spawn continuous ambient particles when clone exists
  useEffect(() => {
    if (!hasClone) {
      setAmbientParticles([])
      return
    }

    // Spawn new particles periodically
    const spawnInterval = setInterval(() => {
      const newParticle: Particle = {
        id: ambientIdRef.current++,
        // Spawn along the top edge of the portrait
        x: width * 0.2 + Math.random() * width * 0.6,
        y: -5 + Math.random() * 10,
        vx: (Math.random() - 0.5) * 0.8,
        vy: -0.5 - Math.random() * 1.5, // Float upward
        size: 2 + Math.random() * 4,
        opacity: 0.8 + Math.random() * 0.2,
        life: 1,
      }
      setAmbientParticles(prev => [...prev, newParticle])
    }, 100) // Spawn every 100ms

    return () => clearInterval(spawnInterval)
  }, [hasClone, width])

  // Animate ambient particles
  useEffect(() => {
    if (ambientParticles.length === 0) return

    const interval = setInterval(() => {
      setAmbientParticles(prev =>
        prev
          .map(p => ({
            ...p,
            x: p.x + p.vx + Math.sin(Date.now() * 0.01 + p.id) * 0.2, // Gentle wave
            y: p.y + p.vy,
            life: p.life - 0.015,
            opacity: p.life * 0.8,
          }))
          .filter(p => p.life > 0)
      )
    }, 16)

    return () => clearInterval(interval)
  }, [ambientParticles.length])

  const portraitImage = hasClone
    ? "/images/droppod-clone.png"
    : "/images/droppod-empty.png"

  return (
    <div
      id="droppod-portrait-section"
      className="absolute"
      style={{
        left,
        top,
        width,
        height,
      }}
    >
      <div className="w-full h-full relative overflow-visible">
        <img
          src={portraitImage}
          alt={hasClone ? "Droppod with clone" : "Empty droppod"}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ imageRendering: "pixelated" }}
        />

        {/* Blue particle effects - can overflow to surround the portrait */}
        {particles.map(p => (
          <div
            key={p.id}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: p.x,
              top: p.y,
              width: p.size * effectiveScale,
              height: p.size * effectiveScale,
              backgroundColor: `rgba(100, 180, 255, ${p.opacity})`,
              boxShadow: `0 0 ${p.size * 3}px rgba(100, 180, 255, ${p.opacity * 0.7})`,
              transform: "translate(-50%, -50%)",
            }}
          />
        ))}

        {/* Continuous ambient blue particles at top when clone exists */}
        {ambientParticles.map(p => (
          <div
            key={`ambient-${p.id}`}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: p.x,
              top: p.y,
              width: p.size * effectiveScale,
              height: p.size * effectiveScale,
              backgroundColor: `rgba(80, 160, 255, ${p.opacity})`,
              boxShadow: `0 0 ${p.size * 2}px rgba(80, 160, 255, ${p.opacity * 0.6})`,
              transform: "translate(-50%, -50%)",
            }}
          />
        ))}
      </div>
    </div>
  )
}
